import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, UserPlus, Phone, Mail, Filter, ChevronDown, AlertCircle, Loader2, Users, CreditCard } from 'lucide-react';
import bookingService from '../services/bookingService';
import eventService from '../services/eventService';
import paymentService from '../services/paymentService';
import './MeusAgendamentos.css';
import CustomAlert from '../components/CustomAlert';
import useCustomAlert from '../hooks/useCustomAlert';
import PartyParticipantsModal from '../components/PartyParticipantsModal';

export default function MeusAgendamentos() {
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroCrianca, setFiltroCrianca] = useState('todas');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [agendamentos, setAgendamentos] = useState([]);
  const [eventReservations, setEventReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelando, setCancelando] = useState(null);
  const { alertProps, showSuccess, showError } = useCustomAlert();
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [bookingsData, eventReservationsData] = await Promise.all([
        bookingService.getUserBookings(),
        eventService.getMyReservations()
      ]);
      
      const agora = new Date();
      const agendamentosFuturos = bookingsData.bookings.filter(agendamento => {
        const dataAgendamento = new Date(agendamento.dataHora);
        return dataAgendamento >= agora && (agendamento.status !== 'Pendente de Pagamento' || agendamento.status !== 'Pendente');
      });
      
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      const eventosFuturos = eventReservationsData.filter(evento => {
        const dataEvento = new Date(evento.eventStartDate);
        dataEvento.setHours(0, 0, 0, 0);
        return dataEvento >= hoje && (evento.status !== 'Pendente de Pagamento' || evento.status !== 'Pendente');
      });
      
      setAgendamentos(agendamentosFuturos);
      setUser(bookingsData.user);
      console.log(agendamentosFuturos)
      setEventReservations(eventosFuturos);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.message || 'Erro ao carregar agendamentos');
    } finally {
      setLoading(false);
    }
  };

  const formatarDatasSelecionadas = (selectedDays) => {
    if (!selectedDays || selectedDays.length === 0) return 'Dia único';
    
    return selectedDays.map(dia => {
      const date = new Date(dia);
      return date.toLocaleDateString('pt-PT', {
        day: '2-digit',
        month: 'short'
      });
    }).join(', ');
  };

  const handleOpenParticipantsModal = (agendamento) => {
    setSelectedBooking(agendamento);
    setShowParticipantsModal(true);
  };

  const handleSaveParticipants = async (participants) => {
    try {
      await bookingService.addParticipants(selectedBooking.id, participants);
      showSuccess('Participantes adicionados com sucesso!');
      await carregarDados();
    } catch (err) {
      throw err;
    }
  };

  const handlePayment = async (agendamento) => {
    try {
      const paymentTypeRadio = document.querySelector(`input[name="payment-${agendamento.id}"]:checked`);
      const paymentType = paymentTypeRadio?.value || 'deposit';
      
      const preco = parseFloat(agendamento.preco.replace('€','').replace(',','.'));
      const amountToPay = paymentType === 'full' ? preco : preco / 2;

      const currentUrl = window.location.origin;
      const successUrl = `${currentUrl}/pagamento?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${currentUrl}/meus-agendamentos`;

      const paymentSession = await paymentService.createCheckoutSession(
        agendamento.id,
        amountToPay,
        successUrl,
        cancelUrl,
        paymentType
      );

      window.location.href = paymentSession.sessionUrl;
    } catch (error) {
      console.error('Erro ao processar pagamento:', error);
      showError('Erro ao processar pagamento. Tente novamente.');
    }
  };

  const handlePaymentEvent = async (agendamento) => {
    try {
      const paymentTypeRadio = document.querySelector(`input[name="payment-event-${agendamento.id}"]:checked`);
      const paymentType = paymentTypeRadio?.value || 'deposit';
      
      const amountToPay = paymentType === 'full' ? agendamento.totalPrice : agendamento.totalPrice / 2;

      const currentUrl = window.location.origin;
      const successUrl = `${currentUrl}/pagamento?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${currentUrl}/meus-agendamentos`;

      const paymentSession = await paymentService.createCheckoutSession(
        agendamento.id,
        amountToPay,
        successUrl,
        cancelUrl,
        paymentType
      );

      window.location.href = paymentSession.sessionUrl;
    } catch (error) {
      console.error('Erro ao processar pagamento de evento:', error);
      showError('Erro ao processar pagamento. Tente novamente.');
    }
  };

  const formatarTimeSlots = (timeSlotReservations) => {
    if (!timeSlotReservations || timeSlotReservations.length === 0) return null;

    return timeSlotReservations.map((slot, index) => (
      <div key={index} className="time-slot-item">
        <div className="time-slot-header">
          <span className="time-slot-name">{slot.timeSlotName}</span>
          <span className="time-slot-price">{formatarPreco(slot.price)}</span>
        </div>
        <div className="time-slot-details">
          <span className="time-slot-time">
            <Clock size={14} />
            {slot.startTime} - {slot.endTime}
          </span>
          {slot.specificDate && (
            <span className="time-slot-date">
              {formatarData(slot.specificDate)}
            </span>
          )}
          {slot.day && !slot.specificDate && (
            <span className="time-slot-date">
              {formatarData(slot.day)}
            </span>
          )}
        </div>
      </div>
    ));
  };

  const handleCancelar = async (id, tipo) => {
    if (!window.confirm('Tem certeza que deseja cancelar este agendamento?')) {
      return;
    }

    try {
      setCancelando(`${tipo}-${id}`);
      
      if (tipo === 'servico') {
        await bookingService.cancelBooking(id);
      } else if (tipo === 'evento') {
        await eventService.cancelReservation(id);
      }
      
      await carregarDados();
      showSuccess('Agendamento cancelado com sucesso!');
    } catch (err) {
      console.error('Erro ao cancelar agendamento:', err);
      showError(err.message || 'Erro ao cancelar agendamento');
    } finally {
      setCancelando(null);
    }
  };

  const todosAgendamentos = [
    ...agendamentos.map(ag => ({ ...ag, tipo: 'servico' })),
    ...eventReservations.map(ev => ({ ...ev, tipo: 'evento' }))
  ];

  const participants = [...new Set(
    todosAgendamentos.flatMap(ag => 
      ag.participants ? ag.participants.map(c => c.nome) : []
    )
  )];

  const agendamentosFiltrados = todosAgendamentos.filter(ag => {
    const matchStatus = filtroStatus === 'todos' || ag.status === filtroStatus;
    const matchCrianca = filtroCrianca === 'todas' || 
      (ag.participants && ag.participants.some(c => c.nome === filtroCrianca));
    const matchTipo = filtroTipo === 'todos' || ag.tipo === filtroTipo;
    
    return matchStatus && matchCrianca && matchTipo;
  });

  const getStatusClass = (status, servicoTipo) => {
    // Para Pilates e Desenho pendentes, usar estilo de confirmado
    if ((status === 'Pendente' || status === 'Pendente de Pagamento') && 
        (servicoTipo === 'Pilates' || servicoTipo === 'Desenho')) {
      return 'status-confirmed';
    }
    
    switch(status) {
      case 'Confirmado': return 'status-confirmed';
      case 'Pendente': return 'status-pending';
      case 'Concluido': return 'status-completed';
      case 'Cancelado': return 'status-cancelled';
      default: return 'status-default';
    }
  };

  const getStatusTexto = (status, servicoTipo) => {
    // Para Pilates e Desenho, mostrar "Agendado" em vez de "Pendente"
    if ((status === 'Pendente' || status === 'Pendente de Pagamento') && 
        (servicoTipo === 'Pilates' || servicoTipo === 'Desenho')) {
      return 'Agendado';
    }
    return status || 'Desconhecido';
  };

  const formatarData = (data) => {
    const date = new Date(data);
    return date.toLocaleDateString('pt-PT', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const formatarHora = (data) => {
    const date = new Date(data);
    return date.toLocaleTimeString('pt-PT', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const calcularIdade = (dataNascimento) => {
    const hoje = new Date();
    const nascimento = new Date(dataNascimento);
    let idade = hoje.getFullYear() - nascimento.getFullYear();
    const mes = hoje.getMonth() - nascimento.getMonth();
    if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
      idade--;
    }
    return idade;
  };

  const formatarPreco = (preco) => {
    if (typeof preco === 'number') {
      return new Intl.NumberFormat('pt-PT', {
        style: 'currency',
        currency: 'EUR'
      }).format(preco);
    }
    return preco;
  };

  const getTipoAgendamento = (agendamento) => {
    return agendamento.tipo === 'evento' ? 'Evento' : 'Serviço';
  };

  const getIconeTipo = (agendamento) => {
    return agendamento.tipo === 'evento' ? <Users size={20} /> : <Calendar size={20} />;
  };

  if (loading) {
    return (
      <div className="agendamentos-container loading-container">
        <div className="loading-content">
          <Loader2 className="loading-spinner" />
          <p>Carregando agendamentos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="agendamentos-container error-container">
        <div className="error-content">
          <AlertCircle className="error-icon" />
          <h3>Erro ao carregar agendamentos</h3>
          <p>{error}</p>
          <button onClick={carregarDados} className="btn-retry">
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="agendamentos-container">
      <div className="agendamentos-wrapper">

        <div className="agendamentos-header">
          <h1>Meus Agendamentos</h1>
          <p>Gerencie os agendamentos de serviços e eventos das suas crianças</p>
        </div>

        {todosAgendamentos.length > 0 && (
          <div className="filtros-card">
            <button
              onClick={() => setMostrarFiltros(!mostrarFiltros)}
              className="filtros-toggle"
            >
              <div className="filtros-toggle-left">
                <Filter size={20} />
                <span>Filtros</span>
              </div>
              <ChevronDown 
                size={20}
                className={`filtros-chevron ${mostrarFiltros ? 'rotated' : ''}`} 
              />
            </button>

            {mostrarFiltros && (
              <div className="filtros-content">
                <div className="filtro-group">
                  <label>Tipo</label>
                  <select
                    value={filtroTipo}
                    onChange={(e) => setFiltroTipo(e.target.value)}
                  >
                    <option value="todos">Todos os Tipos</option>
                    <option value="servico">Serviços</option>
                    <option value="evento">Eventos</option>
                  </select>
                </div>

                <div className="filtro-group">
                  <label>Participantes</label>
                  <select
                    value={filtroCrianca}
                    onChange={(e) => setFiltroCrianca(e.target.value)}
                  >
                    <option value="todas">Todos</option>
                    {participants.map(crianca => (
                      <option key={crianca} value={crianca}>{crianca}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="agendamentos-list">
          {agendamentosFiltrados.length === 0 ? (
            <div className="empty-state">
              <Calendar size={64} />
              <h3>
                {todosAgendamentos.length === 0 
                  ? 'Nenhum agendamento encontrado'
                  : 'Nenhum agendamento com os filtros selecionados'
                }
              </h3>
              <p>
                {todosAgendamentos.length === 0 
                  ? 'Você ainda não tem agendamentos.'
                  : 'Tente ajustar os filtros para ver mais resultados.'
                }
              </p>
            </div>
          ) : (
            agendamentosFiltrados.map((agendamento) => (
              <div key={`${agendamento.tipo}-${agendamento.id}`} className={`agendamento-card ${agendamento.tipo}-card`}>
                <div className="agendamento-header">
                  <div className="agendamento-type-badge">
                    {getIconeTipo(agendamento)}
                    <span>{getTipoAgendamento(agendamento)}</span>
                  </div>
                  <div className={`agendamento-status ${getStatusClass(agendamento.status, agendamento.servicoTipo)}`}>
                    {getStatusTexto(agendamento.status, agendamento.servicoTipo)}
                  </div>
                </div>

                <div className="agendamento-child-info">
                  <div className="child-avatar">
                    <User size={24} />
                  </div>
                  <div>
                    <h3>
                      {agendamento.participants && agendamento.participants.length > 0
                        ? agendamento.participants.map(c => c.nome).join(', ')
                        : user.nome || 'Sem participante'}
                    </h3>
                    <p>
                      {agendamento.participants && agendamento.participants.length > 0
                        ? agendamento.participants.map(c => 
                            `${calcularIdade(c.dataNascimento)} anos`
                          ).join(', ')
                        : agendamento.userName ? 'Cliente' : ''}
                    </p>
                  </div>
                </div>
                {(agendamento.tipo === 'servico' || 
                    !agendamento.timeSlotReservations || 
                    agendamento.timeSlotReservations.length === 0) && (
                  <div className="agendamento-datetime">                                      
                      <div className="datetime-item">
                        <Calendar size={20} />
                        <span>
                          {formatarData(agendamento.tipo === 'evento' ? agendamento.eventStartDate : agendamento.dataHora)}
                        </span>
                      </div>
                      <div className="datetime-item">
                        <Clock size={20} />
                        <span>
                          {formatarHora(agendamento.tipo === 'evento' ? agendamento.eventStartDate : agendamento.dataHora)}
                          {agendamento.duracao && ` (${agendamento.duracao})`}
                        </span>
                      </div>
                    
                  </div>
                )}

                <div className="agendamento-details">
                  {agendamento.tipo === 'evento' ? (
                  <>
                    <div className="detail-section">
                      <p className="detail-label">Evento</p>
                      <p className="detail-value">{agendamento.eventTitle}</p>
                    </div>
                    
                    {/* NOVA SECÇÃO: Time Slots */}
                    {agendamento.timeSlotReservations && agendamento.timeSlotReservations.length > 0 && (
                      <div className="detail-section">
                        <p className="detail-label">Horários Selecionados</p>
                        <div className="time-slots-container">
                          {formatarTimeSlots(agendamento.timeSlotReservations)}
                        </div>
                      </div>
                    )}
                    
                    {agendamento.selectedDays && agendamento.selectedDays.length > 0 ? (
                      <div className="detail-section">
                        <p className="detail-label">Datas de Participação</p>
                        <p className="detail-value">
                          {formatarDatasSelecionadas(agendamento.selectedDays)}
                        </p>
                      </div>
                    ) : (
                      <div className="detail-section">
                        <p className="detail-label">Data</p>
                        <p className="detail-value">
                          {formatarData(agendamento.eventStartDate)}
                        </p>
                      </div>
                    )}

                    {agendamento.totalPrice && 
                      !(agendamento.status === 'Pendente' || agendamento.status === 'Pendente de Pagamento') && (
                        <>
                          <div className="detail-section">
                            <p className="detail-label">Preço Total</p>
                            <p className="detail-value highlight">
                              {formatarPreco(agendamento.totalPrice)}
                            </p>
                          </div>
                          <div className="detail-section">
                            <p className="detail-label">Tipo de Pagamento</p>
                            <p className="detail-value" style={{
                              color: agendamento.paymentType === 'full' ? '#10b981' : '#f59e0b',
                              fontWeight: 600
                            }}>
                              {agendamento.paymentType === 'full' 
                                ? 'Pago Totalmente' 
                                : 'Caução (50%)'}
                            </p>
                          </div>
                          {agendamento.paymentType === 'deposit' && (
                            <>
                              <div className="detail-section">
                                <p className="detail-label">Valor Pago</p>
                                <p className="detail-value" style={{ color: '#10b981', fontWeight: 600 }}>
                                  €{(agendamento.totalPrice / 2).toFixed(2)}
                                </p>
                              </div>
                              <div className="detail-section">
                                <p className="detail-label">A Pagar no Dia</p>
                                <p className="detail-value" style={{ color: '#f59e0b', fontWeight: 600 }}>
                                  €{(agendamento.totalPrice / 2).toFixed(2)}
                                </p>
                              </div>
                            </>
                          )}
                        </>
                      )}

                    {agendamento.eventEndDate && agendamento.selectedDays && agendamento.selectedDays.length === 0 && (
                      <div className="detail-section">
                        <p className="detail-label">Data de Término</p>
                        <p className="detail-value">{formatarData(agendamento.eventEndDate)}</p>
                      </div>
                    )}

                    {/* NOVA SECÇÃO: Pagamento Pendente para EVENTOS */}
                    {(agendamento.status === 'Pendente' || agendamento.status === 'Pendente de Pagamento')&& agendamento.tipo === 'evento' && agendamento.totalPrice && (
                      <div className="payment-section" style={{ 
                        marginTop: '1rem',
                        padding: '1rem',
                        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                        borderRadius: '8px',
                        border: '2px solid #f59e0b'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          marginBottom: '0.75rem',
                          color: '#92400e',
                          fontWeight: 600
                        }}>
                          <AlertCircle size={20} />
                          <span>Pagamento Pendente</span>
                        </div>
                        
                        <div className="payment-options" style={{ marginBottom: '1rem' }}>
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            padding: '0.75rem',
                            background: 'white',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            marginBottom: '0.5rem',
                            border: '2px solid transparent',
                            transition: 'all 0.3s'
                          }}>
                            <input
                              type="radio"
                              name={`payment-event-${agendamento.id}`}
                              value="deposit"
                              defaultChecked
                              style={{ marginRight: '8px' }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, color: '#374151' }}>Caução (50%)</div>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                Pagar €{(agendamento.totalPrice / 2).toFixed(2)} agora
                              </div>
                            </div>
                          </label>
                          
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            padding: '0.75rem',
                            background: 'white',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            border: '2px solid transparent',
                            transition: 'all 0.3s'
                          }}>
                            <input
                              type="radio"
                              name={`payment-event-${agendamento.id}`}
                              value="full"
                              style={{ marginRight: '8px' }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, color: '#374151' }}>Pagamento Total</div>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                Pagar €{agendamento.totalPrice.toFixed(2)} agora
                              </div>
                            </div>
                          </label>
                        </div>

                        <button
                          onClick={() => handlePaymentEvent(agendamento)}
                          style={{
                            width: '100%',
                            padding: '0.875rem',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            transition: 'transform 0.2s'
                          }}
                          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                          onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                        >
                          <CreditCard size={20} />
                          Efetuar Pagamento
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                    <>
                      <div className="detail-section">
                        <p className="detail-label">Serviço</p>
                        <p className="detail-value">{agendamento.servicoNome}</p>
                      </div>

                      {agendamento.numeroParticipantes && (
                        <div className="detail-section">
                          <p className="detail-label">Número de Participantes</p>
                          <p className="detail-value">{agendamento.numeroParticipantes}</p>
                        </div>
                      )}

                      {agendamento.preco && 
                        !(agendamento.status === 'Pendente' || agendamento.status === 'Pendente de Pagamento') &&
                        agendamento.servicoTipo !== 'Pilates' && 
                        agendamento.servicoTipo !== 'Desenho' && (
                          <>
                            <div className="detail-section">
                              <p className="detail-label">Preço Total</p>
                              <p className="detail-value">{agendamento.preco}</p>
                            </div>
                            <div className="detail-section">
                              <p className="detail-label">Tipo de Pagamento</p>
                              <p className="detail-value" style={{
                                color: agendamento.paymentType === 'full' ? '#10b981' : '#f59e0b',
                                fontWeight: 600
                              }}>
                                {agendamento.paymentType === 'full' 
                                  ? 'Pago Totalmente' 
                                  : 'Caução (50%)'}
                              </p>
                            </div>
                            {agendamento.paymentType === 'deposit' && (
                              <>
                                <div className="detail-section">
                                  <p className="detail-label">Valor Pago</p>
                                  <p className="detail-value" style={{ color: '#10b981', fontWeight: 600 }}>
                                    €{(parseFloat(agendamento.preco.replace('€','').replace(',','.')) / 2).toFixed(2)}
                                  </p>
                                </div>
                                <div className="detail-section">
                                  <p className="detail-label">A Pagar no Dia</p>
                                  <p className="detail-value" style={{ color: '#f59e0b', fontWeight: 600 }}>
                                    €{(parseFloat(agendamento.preco.replace('€','').replace(',','.')) / 2).toFixed(2)}
                                  </p>
                                </div>
                              </>
                            )}
                          </>
                        )}

                      {/* Mensagem para Pilates e Desenho - sem pagamento */}
                      {(agendamento.servicoTipo === 'Pilates' || agendamento.servicoTipo === 'Desenho') && (
                        <div className="info-section" style={{ 
                          marginTop: '1rem',
                          padding: '1rem',
                          background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
                          borderRadius: '8px',
                          border: '2px solid #0ea5e9'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            color: '#0c4a6e',
                            fontWeight: 600,
                            fontSize: '0.95rem'
                          }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10"></circle>
                              <line x1="12" y1="16" x2="12" y2="12"></line>
                              <line x1="12" y1="8" x2="12.01" y2="8"></line>
                            </svg>
                            <span>Esta atividade não requer pagamento online</span>
                          </div>
                          <p style={{ 
                            margin: '0.5rem 0 0 28px',
                            fontSize: '0.875rem',
                            color: '#075985',
                            lineHeight: '1.5'
                          }}>
                            O pagamento será efetuado na hora.
                          </p>
                        </div>
                      )}


                      {(agendamento.status === 'Pendente' || agendamento.status === 'Pendente de Pagamento') && 
                       agendamento.tipo === 'servico' && 
                       agendamento.servicoTipo !== 'Pilates' && 
                       agendamento.servicoTipo !== 'Desenho' && (
                        <div className="payment-section" style={{ 
                          marginTop: '1rem',
                          padding: '1rem',
                          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                          borderRadius: '8px',
                          border: '2px solid #f59e0b'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            marginBottom: '0.75rem',
                            color: '#92400e',
                            fontWeight: 600
                          }}>
                            <AlertCircle size={20} />
                            <span>Pagamento Pendente</span>
                          </div>
                          
                          <div className="payment-options" style={{ marginBottom: '1rem' }}>
                            <label style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              padding: '0.75rem',
                              background: 'white',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              marginBottom: '0.5rem',
                              border: '2px solid transparent',
                              transition: 'all 0.3s'
                            }}>
                              <input
                                type="radio"
                                name={`payment-${agendamento.id}`}
                                value="deposit"
                                defaultChecked
                                style={{ marginRight: '8px' }}
                              />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, color: '#374151' }}>Caução (50%)</div>
                                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                  Pagar €{(parseFloat(agendamento.preco.replace('€','').replace(',','.')) / 2).toFixed(2)} agora
                                </div>
                              </div>
                            </label>
                            
                            <label style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              padding: '0.75rem',
                              background: 'white',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              border: '2px solid transparent',
                              transition: 'all 0.3s'
                            }}>
                              <input
                                type="radio"
                                name={`payment-${agendamento.id}`}
                                value="full"
                                style={{ marginRight: '8px' }}
                              />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, color: '#374151' }}>Pagamento Total</div>
                                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                  Pagar {agendamento.preco} agora
                                </div>
                              </div>
                            </label>
                          </div>

                          <button
                            onClick={() => handlePayment(agendamento)}
                            style={{
                              width: '100%',
                              padding: '0.875rem',
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px',
                              transition: 'transform 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                          >
                            <CreditCard size={20} />
                            Efetuar Pagamento
                          </button>
                        </div>
                      )}

                      {agendamento.servicoTipo === 'FestaAniversario' && (
                        <div className="detail-section">
                          <button 
                            className={`participants-button ${agendamento.hasParticipants ? 'has-participants' : 'no-participants'}`}
                            onClick={() => handleOpenParticipantsModal(agendamento)}
                          >
                            <UserPlus size={18} />
                            {agendamento.hasParticipants 
                              ? `Ver Participantes (${agendamento.partyParticipants?.length || 0})`
                              : 'Adicionar Participantes'
                            }
                          </button>
                          {!agendamento.hasParticipants && (
                            <p className="participants-warning">
                              <AlertCircle size={14} />
                              Por favor, adicione a lista de participantes
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {agendamento.observacoes && (
                    <div className="detail-section">
                      <p className="detail-label">Observações</p>
                      <p className="detail-notes">{agendamento.observacoes}</p>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {agendamentosFiltrados.length > 0 && (
          <div className="agendamentos-resumo">
            <p>
              <strong>{agendamentosFiltrados.length}</strong> agendamento(s) encontrado(s) :
              <span className="resumo-detalhes">
                {' '}{agendamentosFiltrados.filter(a => a.tipo === 'servico').length} serviço(s), 
                {' '}{agendamentosFiltrados.filter(a => a.tipo === 'evento').length} evento(s)
              </span>
            </p>
          </div>
        )}
      </div>
      <CustomAlert {...alertProps} />

      <PartyParticipantsModal
        isOpen={showParticipantsModal}
        onClose={() => setShowParticipantsModal(false)}
        booking={selectedBooking}
        onSave={handleSaveParticipants}
      />
    </div>
  );
}